import React, {
  RefObject,
  useEffect,
  useState,
} from 'react';
import Table from 'core/components/VirtualTable';
import isNil from 'lodash/isNil';
import {
  useTableReset,
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  VirtualTableInstance,
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from 'core/icons/delete';
import {
  getTags,
} from 'views/EMR/Settings/transport';
import SearchIcon from 'core/icons/search';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  useDeleteTag,
} from 'views/EMR/Settings/hooks';
import ToolTip from 'core/components/Tooltip';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import TagDialog, {
  TagDialogProps,
} from 'views/EMR/Settings/Tags/Dialog';
import {
  TagDTO,
} from 'dtos/tagDTO';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  formatDate,
} from 'utils/date';
import Tag from 'core/components/Tag';
import Name from 'pages/Dashboard/components/Name';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import clsx from 'clsx';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';

type TagActionsCellProps = {
  tagId?: number | null;
  name?: string | null;
  userId?: number | null;
};

function TagActionsCell({ tagId, name, userId }: TagActionsCellProps) {
  const { mutateAsync: deleteTag } = useDeleteTag();
  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();
  const userInfo = useUserInfo();
  const disabled = userId !== userInfo?.user?.userId;

  const handleTagDelete = () => {
    confirm({
      content: (
        <div className="mb-2">
          Are you sure you want to delete the tag
          {' '}
          <span className="font-semibold">
            {name}
          </span>
          ?
        </div>
      ),
      title: 'Delete Tag',
      confirmText: 'Delete',
      onConfirm: async () => {
        await deleteTag({ id: tagId ?? 0 });
        notify({
          variant: 'success',
          message: 'Tag deleted successfully',
        });
        settingsEventChannel.emit('onTagsChange');
        closeConfirm();
      },
    });
  };

  return (
    <ToolTip title={disabled
      ? (
        <>
          You are not allowed to delete tags
          <br />
          that are created by another user.
        </>
      ) : 'Delete tag'}
    >
      <div className={clsx({ 'cursor-not-allowed opacity-50': disabled })}>
        <IconButton
          aria-label="Delete Tag"
          size="small"
          className="!p-1"
          disabled={disabled}
          onClick={async (e) => {
            e.stopPropagation();
            handleTagDelete();
          }}
          color="error"
        >
          <DeleteIcon className="!w-5 !h-5 !text-black" />
        </IconButton>
      </div>
    </ToolTip>
  );
}

type CreatedByCellProps = {
  user?: TagDTO['user'];
};

function CreatedByCell({ user }: CreatedByCellProps) {
  const src = useUserProfilePicturePath(user?.userId);

  return (
    <Name
      {...user?.formattedNames}
      image={{ size: 'xs', src }}
    />
  );
}

const columns: ColumnDef<TagDTO>[] = [{
  id: 'name',
  accessorKey: 'name',
  columnTitle: 'Tag',
  size: 250,
  cell: ({ row: { original: { name } } }) => (
    <Tag
      tagName={name ?? ''}
      className="!bg-gray-100 !text-gray-700 !font-semibold"
    />
  ),
}, {
  id: 'createdBy',
  columnTitle: 'Created by',
  size: 200,
  cell: ({ row: { original: { user } } }) => <CreatedByCell user={user} />,
}, {
  id: 'createdAt',
  columnTitle: 'Created on',
  size: 100,
  cell: ({ row: { original: { createdOn } } }) => (
    <div>
      {formatDate(createdOn ?? '')}
    </div>
  ),
}, {
  id: 'actions',
  header: '',
  size: 48,
  cell: ({ row: { original } }) => (
    <TagActionsCell
      tagId={original.tagId}
      name={original.name}
      userId={original.userId}
    />
  ),
}];

export default function Tags() {
  const { tableInstanceRef, reset } = useTableReset();

  const [
    tagDialogParams,
    setTagDialogParams,
  ] = useState<TagDialogProps | null>(null);
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery();

  useEffect(() => {
    const unsubscribeFromTagsChange = settingsEventChannel.on('onTagsChange', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    return () => {
      unsubscribeFromTagsChange();
    };
  }, []);

  const userInfo = useUserInfo();

  return (
    <div className="h-full w-full flex flex-col gap-4">
      <div className="flex gap-4 px-4">
        <Button
          className="!h-8"
          color="primary"
          variant="contained"
          startIcon={<AddIcon className="!w-4 !h-4" />}
          onClick={() => setTagDialogParams({ name: '' })}
        >
          Add Tag
        </Button>
        <SearchInput
          key="tag-search"
          placeholder="Enter Tag Name..."
          className="w-96 h-7"
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            reset();
          }}
          icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
          id="tag-search"
        />
      </div>
      <div className=" relative flex flex-col flex-1 overflow-y-hidden">
        <div className="h-full w-full">
          <Table<TagDTO>
            defaultPagination={{
              pageSize: 25,
              pageIndex: 0,
            }}
            queryParams={{
              searchQuery: searchQuery.trim(),
            }}
            dataKey="tags-table-data"
            instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
            getData={getTags}
            getRowId={(row) => row?.tagId}
            columns={columns}
            emptySlot="No Tags Found"
            classes={{
              header: 'px-4 !justify-start',
              scrollContainer: 'justify-center items-center',
              outerRow: '!w-[calc(100%-16px)] left-4',
              row: '!items-center py-1 !justify-start',
            }}
            onRowClick={(table, row) => {
              if (row.original?.userId === userInfo?.user?.userId) {
                setTagDialogParams(row.original);
              }
            }}
          />
        </div>
      </div>
      {!isNil(tagDialogParams) && (
        <TagDialog
          {...tagDialogParams}
          onClose={() => { setTagDialogParams(null); }}
        />
      )}
    </div>
  );
}
